import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, ChevronDownIcon, MenuIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePricing } from '../contexts/PricingContext';
import { ContactFormPopup } from './ContactFormPopup';
export function HeroVariant43() {
  const {
    language,
    t,
    getPath
  } = useLanguage();
  const {
    price
  } = usePricing();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const handleLanguageChange = (lang: 'en' | 'ru' | 'ua') => {
    const currentPath = window.location.pathname;
    let newPath = currentPath;
    if (currentPath.startsWith('/ru')) newPath = currentPath.substring(3) || '/';else if (currentPath.startsWith('/ua') || currentPath.startsWith('/uk')) newPath = currentPath.substring(3) || '/';
    if (lang === 'ru') newPath = `/ru${newPath === '/' ? '' : newPath}`;else if (lang === 'ua') newPath = `/ua${newPath === '/' ? '' : newPath}`;
    window.location.href = newPath;
  };
  const content = {
    ru: {
      title1: 'Сделаем сайт для вас бесплатно.',
      title2: `Обслуживание — ${price}/мес.`,
      sub: `Мы делаем сайт за свой счёт. Вы смотрите, просите правки, утверждаете. Когда всё нравится — платите ${price} в месяц за хостинг и поддержку. Не понравится — просто уходите. Без оплаты.`,
      cta: 'Получить бесплатный макет'
    },
    ua: {
      title1: 'Зробимо сайт для вас безкоштовно.',
      title2: `Обслуговування — ${price}/міс.`,
      sub: `Ми робимо сайт за свій рахунок. Ви дивитеся, просите правки, затверджуєте. Коли все подобається — платите ${price} на місяць за хостинг та підтримку. Не сподобається — просто йдете. Без оплати.`,
      cta: 'Отримати безкоштовний макет'
    },
    en: {
      title1: 'We build your website for free.',
      title2: `Maintenance — ${price}/mo.`,
      sub: `We build the site at our expense. You review, request edits, and approve. When you love it, you pay ${price}/month for hosting and support. Don't like it? Just walk away. No charge.`,
      cta: 'Get a free mockup'
    }
  }[language] || content.ru;
  return <>
      <section className="relative w-full h-screen min-h-[800px] flex flex-col overflow-hidden bg-black" data-id="element-674">
        {/* Full Screen Video Background */}
        <video src="https://cdn.magicpatterns.com/uploads/vrXe21wkVqS62d5BzpZ9eJ/original-2de319f4767c5a0d4acf4f107b938504.mp4" autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover opacity-60" data-id="element-675" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/20 to-black/80" data-id="element-676" />

        {/* Transparent Header Inside Hero */}
        <header className="relative z-50 w-full pt-6 pb-4 px-4 sm:px-6 lg:px-8" data-id="element-677">
          <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-678">
            <a href={getPath('')} className="flex items-center gap-1 text-white" data-id="element-679">
              <span className="text-2xl font-bold tracking-tight" data-id="element-680">J</span>
              <span className="w-2 h-2 rounded-full bg-blue-500" data-id="element-681" />
            </a>

            <div className="hidden md:flex items-center gap-8" data-id="element-682">
              <a href="#how-it-works" className="text-sm font-semibold text-white/90 hover:text-white transition-colors" data-id="element-683">
                {t('nav.howItWorks')}
              </a>
              <a href="#services" className="text-sm font-semibold text-white/90 hover:text-white transition-colors" data-id="element-684">
                {t('nav.pricing')}
              </a>
              <a href="#faq" className="text-sm font-semibold text-white/90 hover:text-white transition-colors" data-id="element-685">
                {t('nav.faq')}
              </a>
            </div>

            <div className="flex items-center gap-4" data-id="element-686">
              <div className="hidden md:block relative" data-id="element-687">
                <button onClick={() => setIsLangMenuOpen(!isLangMenuOpen)} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-all backdrop-blur-md border border-white/10" data-id="element-688">
                  <span className="uppercase" data-id="element-689">{language}</span>
                  <ChevronDownIcon className="w-4 h-4" data-id="element-690" />
                </button>
                <AnimatePresence data-id="element-691">
                  {isLangMenuOpen && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} className="absolute top-full mt-2 right-0 rounded-lg bg-white/10 backdrop-blur-xl border border-white/20 overflow-hidden shadow-2xl min-w-[80px]" data-id="element-692">
                      {['en', 'ru', 'ua'].map(lang => <button key={lang} onClick={() => handleLanguageChange(lang as any)} className={`w-full px-4 py-2 text-left text-sm font-medium transition-colors ${language === lang ? 'bg-blue-600 text-white' : 'text-white hover:bg-white/10'}`} data-id="element-693">
                          {lang.toUpperCase()}
                        </button>)}
                    </motion.div>}
                </AnimatePresence>
              </div>
              <button onClick={() => setIsPopupOpen(true)} className="hidden md:block px-6 py-2.5 rounded-lg text-sm font-semibold transition-all hover:scale-105 bg-blue-600 text-white border border-blue-500 shadow-lg shadow-blue-500/30" data-id="element-694">
                {t('nav.getStarted')}
              </button>
              <button className="md:hidden p-2 text-white" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-695">
                {isMobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-696" /> : <MenuIcon className="w-6 h-6" data-id="element-697" />}
              </button>
            </div>
          </div>
        </header>

        {/* Centered Content */}
        <div className="relative z-10 flex-1 flex items-center justify-center px-4" data-id="element-698">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="bg-black/40 backdrop-blur-2xl border border-white/10 p-8 md:p-14 rounded-[2rem] text-center max-w-4xl shadow-2xl" data-id="element-699">
            <h1 className="mb-4 text-4xl md:text-5xl lg:text-[4rem] font-black leading-tight text-white tracking-tight" data-id="element-700">
              {content.title1}
            </h1>
            <h2 className="mb-8 text-2xl md:text-3xl lg:text-4xl font-bold text-blue-400" data-id="element-701">
              {content.title2}
            </h2>
            <p className="text-lg md:text-xl mb-10 text-gray-300 font-medium leading-relaxed max-w-3xl mx-auto" data-id="element-702">
              {content.sub}
            </p>
            <button onClick={() => setIsPopupOpen(true)} className="group inline-flex items-center justify-center gap-2 rounded-full px-10 py-5 font-bold text-lg transition-all hover:scale-105 bg-blue-600 text-white shadow-xl shadow-blue-600/30" data-id="element-703">
              {content.cta}
              <ArrowRightIcon className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-704" />
            </button>
          </motion.div>
        </div>
      </section>
      <ContactFormPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} data-id="element-705" />
    </>;
}